import {useAppContext} from "@context/appContext/hooks/useAppContext";
import {useAuthContext} from "@containers/auth/context/hooks/useAuthContext";
import {checkToken, getUserInfo} from "@containers/auth/helpers/helpers";
import {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {buildAuthoritiesMap} from "@containers/auth/helpers/buildAuthoritiesMap";
import {retryWhileApiUnavailable} from "@helpers/query/helpers/retryWhileApiUnavailable";

export function useAuthInitWidthTrack(): {
    isLoading: boolean,
} {

    const [isLoading, setIsLoading] = useState(true)

    const {
        setUser,
        setAccount,
        userBean,
        setAuthorities,
    } = useAppContext()
    const {setIsAuth} = useAuthContext()

    useEffect(() => {
        let cancelled = false
        const isCancelled = () => cancelled

        const authFn = async () => {
            setIsLoading(true)
            try {
                const tokens = await retryWhileApiUnavailable(checkToken, isCancelled)
                if (!tokens || !tokens?.refreshToken) {
                    setIsLoading(false)
                    setIsAuth(false)
                    return
                }
                const res = await retryWhileApiUnavailable(getUserInfo, isCancelled)
                if (!res) {
                    setIsLoading(false)
                    return
                }
                const id = userBean?.type === 'ADMIN' ? userBean.id : res.userId;

                setUser((prev) => {
                    return {
                        ...prev,
                        active:res.active,
                        id,
                        name: res?.userName,
                        contactName: res?.contactName,
                        region: res?.region,
                        timeZone: res?.userTimeZone,
                        multiAccount: res?.userFromMultiAccount,
                        superTenantAccount: res?.userFromSuperTenantAccount,
                        tenantAccount: res?.userFromTenantAccount,
                    }
                })
                setAccount(() => {
                    return {
                        id: res?.accountId,
                        code: res?.accountCode,
                        labelName: res?.accountLabelName,
                        hasSingleVoyage:res.userFromSV || false
                    }
                })
                const authoritiesMap = buildAuthoritiesMap(res?.authorities)
                setAuthorities(authoritiesMap)
                setIsAuth(true)
                setIsLoading(false)
            } catch (e) {
                if (cancelled) {
                    return
                }
                setIsLoading(false)
                setIsAuth(false)
            }
        }
        authFn();

        return () => {
            cancelled = true
        }
    }, [])

    return {
        isLoading,
    }
}

export function useAuthInitSeparated():{
    isLoading: boolean,
}{

    const location = useLocation();

    const [isLoading, setIsLoading] = useState(true)

    const {
        setUser,
        setAccount,
        setAuthorities
    } = useAppContext()
    const {
        isAuth,
        setIsAuth,
    } = useAuthContext()


    useEffect(() => {
        let cancelled = false
        const isCancelled = () => cancelled

        const authFn = async () => {

            setIsLoading(true)
            try {
                const tokens = await retryWhileApiUnavailable(checkToken, isCancelled)
                if (!tokens || !tokens?.refreshToken) {
                    setIsLoading(false)
                    setIsAuth(false)
                    return
                }

                const res = await retryWhileApiUnavailable(getUserInfo, isCancelled)
                if (!res || !res?.userId) {
                    setIsLoading(false)
                    return
                }

                setUser({
                    active:res.active,
                    id: res?.userId,
                    name: res?.userName,
                    contactName: res?.contactName,
                    region: res?.region,
                    timeZone: res?.userTimeZone,
                    multiAccount: res?.userFromMultiAccount,
                    superTenantAccount: res?.userFromSuperTenantAccount,
                    tenantAccount: res?.userFromTenantAccount,
                })

                setAccount({
                    id: res?.accountId,
                    code: res?.accountCode,
                    labelName: res?.accountLabelName,
                    hasSingleVoyage:res.userFromSV || false

                })
                const authoritiesMap = buildAuthoritiesMap(res?.authorities)
                setAuthorities(authoritiesMap)
                setIsAuth(true)
                setIsLoading(false)
            } catch (e) {
                if (cancelled) {
                    return
                }
                setIsLoading(false)
                setIsAuth(false)
            }
        }
        if (!isAuth && location.pathname !== '/login') {
            authFn();
        }else{
            setIsLoading(false)
        }

        return () => {
            cancelled = true
        }
    }, [])

    return {
        isLoading,
    }
}
